import { useState, useEffect, useRef, useCallback } from 'react';

const INTERVAL = 4000;

const getVisible = () => {
  if (window.innerWidth <= 600) return 1;
  if (window.innerWidth <= 900) return 2;
  return 3;
};

const NewsCarousel = ({ children }) => {
  const items = Array.isArray(children) ? children : [children];
  const total = items.length;

  const [visible, setVisible] = useState(getVisible);
  const [index, setIndex] = useState(() => getVisible()); // start at first real slide
  const [animated, setAnimated] = useState(true);
  const pausedRef = useRef(false);
  const timerRef = useRef(null);
  const indexRef = useRef(getVisible());

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    const onResize = () => setVisible(getVisible());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const needsScroll = total > visible;

  // Infinite loop: clone `visible` items at each end
  const cloned = needsScroll
    ? [...items.slice(-visible), ...items, ...items.slice(0, visible)]
    : items;

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    if (!needsScroll || pausedRef.current) return;
    timerRef.current = setInterval(() => {
      setAnimated(true);
      setIndex(i => { const n = i + 1; indexRef.current = n; return n; });
    }, INTERVAL);
  }, [needsScroll]);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  // After disabling animation for snap, re-enable on next frame
  useEffect(() => {
    if (!animated) {
      const id = requestAnimationFrame(() =>
        requestAnimationFrame(() => setAnimated(true))
      );
      return () => cancelAnimationFrame(id);
    }
  }, [animated, index]);

  const handleTransitionEnd = () => {
    if (!needsScroll) return;
    const i = indexRef.current;
    const lo = visible;
    const hi = visible + total;
    if (i >= hi) {
      const next = i - total;
      setAnimated(false);
      setIndex(next);
      indexRef.current = next;
    } else if (i < lo) {
      const next = i + total;
      setAnimated(false);
      setIndex(next);
      indexRef.current = next;
    }
  };

  const handlePrev = () => {
    setAnimated(true);
    setIndex(i => { const n = i - 1; indexRef.current = n; return n; });
    startTimer();
  };

  const handleNext = () => {
    setAnimated(true);
    setIndex(i => { const n = i + 1; indexRef.current = n; return n; });
    startTimer();
  };

  const handleMouseEnter = () => {
    pausedRef.current = true;
    clearInterval(timerRef.current);
  };

  const handleMouseLeave = () => {
    pausedRef.current = false;
    startTimer();
  };

  // Dot: real index within original items
  const realIndex = needsScroll
    ? ((index - visible) % total + total) % total
    : 0;

  if (!needsScroll) {
    return (
      <div className="news-carousel">
        <div className="news-carousel-viewport">
          <div className="news-carousel-track" style={{ justifyContent: 'center' }}>
            {items.map((child, i) => (
              <div key={i} className="news-carousel-slide" style={{ flex: `0 0 calc(100% / ${visible})` }}>
                {child}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="news-carousel" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button className="news-carousel-btn prev" onClick={handlePrev} aria-label="Назад">‹</button>
      <div className="news-carousel-viewport">
        <div
          className="news-carousel-track"
          style={{
            transform: `translateX(calc(-${index} * (100% / ${visible})))`,
            transition: animated ? 'transform 0.45s ease' : 'none',
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {cloned.map((child, i) => (
            <div key={i} className="news-carousel-slide" style={{ flex: `0 0 calc(100% / ${visible})` }}>
              {child}
            </div>
          ))}
        </div>
      </div>
      <button className="news-carousel-btn next" onClick={handleNext} aria-label="Вперёд">›</button>
      <div className="news-carousel-dots">
        {items.map((_, i) => (
          <button
            key={i}
            className={`news-carousel-dot${i === realIndex ? ' active' : ''}`}
            onClick={() => {
              setAnimated(true);
              const n = i + visible;
              setIndex(n);
              indexRef.current = n;
              startTimer();
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default NewsCarousel;
